import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import config from 'src/config'

type PageViewPayload = {
  host: string
  path: string
  referrer?: string
  idealoLandingPath?: string
}

const isIdealoReferrer = (referrer: string) => {
  if (!referrer) return false

  try {
    return /(^|\.)idealo\./i.test(new URL(referrer).hostname)
  } catch {
    return false
  }
}

const hasIdealoMarker = (value: string) => value.toLowerCase().includes('idealo')

const getPath = (url?: string) => {
  if (typeof window === 'undefined') return '/'
  if (!url) return window.location.pathname + window.location.search
  if (url.startsWith('/')) return url

  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.pathname + parsed.search
  } catch {
    return window.location.pathname + window.location.search
  }
}

const sendPageView = (payload: PageViewPayload) => {
  const body = JSON.stringify(payload)
  const endpoint = `${config.api_server.online}/tracking/pageview`

  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' })
    if (navigator.sendBeacon(endpoint, blob)) return
  }

  void fetch(endpoint, {
    method: 'POST',
    body,
    headers: {
      'Content-Type': 'application/json',
    },
    keepalive: true,
  }).catch(() => undefined)
}

export function FirstPartyTracking() {
  const router = useRouter()
  const idealoLandingPath = useRef<string | undefined>(undefined)
  const initialReferrer = useRef<string>('')

  useEffect(() => {
    if (typeof window === 'undefined') return

    initialReferrer.current = document.referrer || ''
    if (isIdealoReferrer(initialReferrer.current) || hasIdealoMarker(window.location.search)) {
      idealoLandingPath.current = getPath()
    }

    sendPageView({
      host: window.location.host,
      path: getPath(),
      referrer: initialReferrer.current,
      idealoLandingPath: idealoLandingPath.current,
    })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    let timeout: number | undefined

    const trackRouteChange = (url: string) => {
      if (timeout) window.clearTimeout(timeout)
      timeout = window.setTimeout(() => {
        sendPageView({
          host: window.location.host,
          path: getPath(url),
          referrer: window.location.href,
          idealoLandingPath: idealoLandingPath.current,
        })
      }, 200)
    }

    router.events.on('routeChangeComplete', trackRouteChange)

    return () => {
      router.events.off('routeChangeComplete', trackRouteChange)
      if (timeout) window.clearTimeout(timeout)
    }
  }, [router.events])

  return null
}
